import { useDispatch, useSelector } from "react-redux";

import type { Feature } from "geojson";

import TabSelect, { TabItem } from "@/components/tabSelect";
import Section from "@/components/ui/section";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setCatchmentSettingsTemp } from "@/store/slice/spatialSlice";
import { CatchmentInputBy, ECatchmentInputBy } from "@/types/spatial";

import CatchmentPlace from "../catchmentPlaceSection";
import CatchmentPolygon from "../catchmentPolygon";
import CatchmentDataset from "./catchmentDataset";

const CATCHMENT_DATA_TYPE = (
  isBVTDataForCatchmentEnabled: boolean
): TabItem[] => [
  {
    label: "Location",
    value: "location"
  },
  ...(isBVTDataForCatchmentEnabled
    ? [{ label: "Polygon", value: "polygon" }]
    : []),
  {
    label: "Dataset",
    value: "dataset"
  }
];

interface Props {
  onOpenDatasetTabularModal?: () => void;
}

const CatchmentDataSection = ({ onOpenDatasetTabularModal }: Props) => {
  const dispatch = useDispatch();
  const { catchmentSettingsTemp, polygonSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { isBVTDataForCatchmentEnabled } = useAppFeatures();
  const { deleteAllFeatures, addFeature } = useMap();

  const isValidCatchmentInputBy = (
    value: string
  ): value is CatchmentInputBy => {
    return Object.values(ECatchmentInputBy).includes(
      value as ECatchmentInputBy
    );
  };

  const handleSelectTab = (value: string) => {
    if (!isValidCatchmentInputBy(value)) {
      console.error("Invalid catchment input value:", value);
      return;
    }
    // `value` is narrowed to CatchmentInputBy from this point on.
    dispatch(setCatchmentSettingsTemp({ catchmentInputBy: value }));

    if (!isBVTDataForCatchmentEnabled) return;

    // Mirror the "Define by" dropdown behaviour: only the active tab's
    // data is rendered on the map.
    if (value === ECatchmentInputBy.BY_POLYGON) {
      deleteAllFeatures();
      Object.values(polygonSettingsTemp ?? {}).forEach((polygonSetting) => {
        addFeature(polygonSetting.polygon as Feature);
      });
    } else {
      deleteAllFeatures();
    }
  };

  return (
    <div className="flex flex-col px-4 gap-2">
      <Section label="Input Catchment by">
        <TabSelect
          className="h-[32px]"
          buttonClassName="text-xs h-[24px]"
          wrapperListClassName="w-full"
          tabList={CATCHMENT_DATA_TYPE(isBVTDataForCatchmentEnabled)}
          selected={catchmentSettingsTemp.catchmentInputBy}
          onSelect={handleSelectTab}
        />
      </Section>
      {catchmentSettingsTemp.catchmentInputBy ===
        ECatchmentInputBy.BY_LOCATION && <CatchmentPlace />}
      {catchmentSettingsTemp.catchmentInputBy ===
        ECatchmentInputBy.BY_DATASET && (
        <CatchmentDataset
          onOpenDatasetTabularModal={onOpenDatasetTabularModal}
        />
      )}
      {isBVTDataForCatchmentEnabled &&
        catchmentSettingsTemp.catchmentInputBy ===
          ECatchmentInputBy.BY_POLYGON && <CatchmentPolygon />}
    </div>
  );
};

export default CatchmentDataSection;
